import React from 'react';
import { WheelSafeNumberInput } from '../common/WheelSafeNumberInput';
import {
    Create,
    SimpleForm,
    TextInput,
    BooleanInput,
    SelectInput,
    ReferenceInput,
    AutocompleteInput,
    required,
    minValue,
    maxValue,
    email,
    Toolbar,
    useRedirect,
    useTranslate,
    useNotify,
    useGetIdentity,
    FormDataConsumer,
    useInput,
} from 'react-admin';
// import { useFormContext } from 'react-hook-form';
import { Box, Typography, Alert, Card, CardContent, IconButton } from '@mui/material';
import { Business, Phone, Security, BatteryChargingFull, LocalParking, Info, ArrowBack, Photo, Schedule } from '@mui/icons-material';
import { MultiLanguageFields } from './MultiLanguageFields';
import { HongKongAddressFields } from './HongKongAddressFields';
import { ImageUploadInput } from './ImageUploadInput';
import { MultiImageUploadInput } from './MultiImageUploadInput';
import { OperatingHoursField } from './OperatingHoursField';
import { SiteValidationBanner } from './SiteValidationBanner';
import { SiteSaveButton } from './SiteSaveButton';
import { AmenitiesInput } from './AmenitiesInput';
import { OPERATING_ACCESS_OPTIONS } from '../../constants/operatingAccess';
import { ServerErrorBinder } from '../common/ServerErrorBinder';
import { translateServerMessage } from '../../utils/translateServerMessage';

const SITE_KNOWN_FIELDS = new Set([
    'name', 'vendorId', 'latitude', 'longitude',
    'addressLine1', 'addressLine2', 'postalCode', 'timezone',
    'phone', 'email', 'supportPhone', 'website',
    'accessType', 'isActive', 'parkingType', 'hasCanopy',
    'reservationEnabled', 'emailReceiptEnabled', 'appAccessEnabled', 'kioskAccessEnabled',
    'totalMaxPower', 'gridConnectionCapacity', 'operatingAccess',
    'parkingInfo', 'operatingHours', 'primaryImage', 'images',
    'amenities', 'tags', 'description',
]);

const mapSiteBackendPath = (path: string): string =>
    path.replace(/^body\./, '').replace(/^params\./, '').replace(/^query\./, '');

const siteFieldLabelKey = (field: string): string => {
    const head = field.split('.')[0];
    return SITE_KNOWN_FIELDS.has(head)
        ? `resources.sites.fields.${head}`
        : field;
};

const CreateToolbar = () => {
    return (
        <Toolbar
            sx={{
                backgroundColor: '#FAFAFA',
                borderTop: '1px solid #E0E0E0',
                px: { xs: 2, sm: 3 },
                py: 2,
                mt: 4,
                '& .MuiButton-root': {
                    borderRadius: 2,
                    textTransform: 'none',
                    fontWeight: 600,
                    px: 3,
                    py: 1.5,
                },
            }}
        >
            <SiteSaveButton />
        </Toolbar>
    );
};

const PageHeader = () => {
    const redirect = useRedirect();
    const translate = useTranslate();
    
    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            gap: { xs: 1.5, sm: 2 },
            mb: { xs: 2, sm: 4 },
        }}>
            <IconButton
                onClick={() => redirect('/sites')}
                size="large"
                sx={{
                    backgroundColor: '#FFFFFF',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                    '&:hover': {
                        backgroundColor: '#F5F5F5',
                        boxShadow: '0 4px 12px rgba(0,0,0,0.12)',
                    },
                }}
                title={translate('resources.sites.edit.backToList')}
            >
                <ArrowBack />
            </IconButton>
            <Box sx={{ minWidth: 0 }}>
                <Typography sx={{ fontWeight: 600, mb: 0.5, fontSize: { xs: '1.25rem', sm: '2.125rem' } }}>
                    {translate('resources.sites.create.title')}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontSize: { xs: '0.75rem', sm: '1rem' } }}>
                    {translate('resources.sites.create.subtitle')}
                </Typography>
            </Box>
        </Box>
    );
};

// Temporarily removed to fix infinite loop issue
// const ImageUploadFieldsWrapper = () => {
//     const { setValue, watch } = useFormContext();
//     const translate = useTranslate();
//     
//     const primaryImage = watch('primaryImage');
//     const images = watch('images');
//     
//     return (
//         <>
//             <ImageUploadField
//                 source="primaryImage"
//                 value={primaryImage}
//                 onChange={(value) => setValue('primaryImage', value)}
//                 label={translate('resources.sites.fields.primaryImage')}
//             />
//             
//             <MultiImageUploadField
//                 source="images"
//                 value={images}
//                 onChange={(value) => setValue('images', value)}
//                 label={translate('resources.sites.fields.images')}
//                 maxImages={10}
//             />
//         </>
//     );
// };

export const SiteCreate = () => {
    const translate = useTranslate();
    const notify = useNotify();
    const redirect = useRedirect();
    const { data: identity } = useGetIdentity();
    const [serverErrors, setServerErrors] = React.useState<Record<string, string>>({});
    const validateLatitude = [required(), minValue(-90), maxValue(90)];
    const validateLongitude = [required(), minValue(-180), maxValue(180)];
    const validateRequired = [required()];
    const validateEmail = [email()];

    // Vendor-scope: a merchant-bound admin must attach the new site to one of
    // their vendors. We default the form's vendorId to their first vendor and
    // restrict the autocomplete to that list. Super-admins (no vendor membership)
    // see all vendors and may leave the field blank.
    const actorVendorIds: string[] = ((identity as any)?.vendorIds as string[]) || [];
    const isVendorScoped = actorVendorIds.length > 0;
    const defaultVendorId = isVendorScoped ? actorVendorIds[0] : undefined;

    const transform = (data: any) => {
        // Remove temporary region field
        const { _tempRegion, ...rest } = data;
        // Vendor-scoped admins cannot escape their vendor scope on create — pin
        // vendorId to their first vendor if the form somehow ended up empty.
        if (isVendorScoped && !rest.vendorId) {
            rest.vendorId = actorVendorIds[0];
        }
        return rest;
    };
    
    return (
        <Create 
            title={false}
            component="div"
            redirect={false} // Disable automatic redirect
            mutationMode="pessimistic"
            transform={transform}
            mutationOptions={{
                onSuccess: (data) => {
                    setServerErrors({});
                    notify('resources.sites.notifications.createSuccess', {
                        type: 'success',
                        autoHideDuration: 3000
                    });
                    // Redirect to edit page or list
                    if (data?.id) {
                        redirect('edit', 'sites', data.id);
                    } else {
                        redirect('list', 'sites');
                    }
                },
                onError: (error: any) => {
                    const apiError = error?.body || error;
                    const rawErrors = apiError?.errors as Record<string, string[] | string> | undefined;
                    const fieldErrors: Record<string, string> = {};
                    const summaryLines: string[] = [];
                    if (rawErrors) {
                        for (const [path, msgs] of Object.entries(rawErrors)) {
                            const rawMsg = Array.isArray(msgs) ? msgs[0] : String(msgs);
                            const translated = translateServerMessage(rawMsg, translate);
                            const field = mapSiteBackendPath(path);
                            fieldErrors[field] = translated;
                            const label = translate(siteFieldLabelKey(field), { _: field });
                            summaryLines.push(`• ${label}: ${translated}`);
                        }
                    }
                    setServerErrors(fieldErrors);

                    const headerKey = apiError?.errorCode
                        ? `errors.${apiError.errorCode}`
                        : 'validation.formHasErrors';
                    const header = translate(headerKey, { _: apiError?.message || 'Error' });

                    if (summaryLines.length > 0) {
                        notify(`${header}\n${summaryLines.join('\n')}`, {
                            type: 'error',
                            multiLine: true,
                            autoHideDuration: 10000,
                        });
                    } else {
                        notify(header, { type: 'error', autoHideDuration: 6000 });
                    }
                }
            }}
            sx={{
                backgroundColor: 'transparent',
                '& .RaCreate-main': {
                    backgroundColor: 'transparent',
                    boxShadow: 'none',
                },
            }}
        >
            <SimpleForm
                toolbar={<CreateToolbar />}
                sx={{
                    '& .MuiPaper-root': {
                        backgroundColor: 'transparent',
                        boxShadow: 'none',
                    },
                    '& > .MuiCardContent-root, & > div > .MuiCardContent-root': {
                        px: { xs: 0, sm: 2 },
                        py: { xs: 1, sm: 2 },
                    },
                    padding: { xs: 0, sm: 'inherit' },
                }}
            >
                <ServerErrorBinder errors={serverErrors} />
                <Box sx={{ width: '100%', maxWidth: 800, margin: '0 auto', pb: { xs: 10, sm: 2 }, '& .MuiFormControl-root': { width: '100%' } }}>
                    <PageHeader />

                    <Alert severity="info" sx={{ mb: 3 }}>
                        <Typography variant="body2">
                            {translate('resources.sites.create.infoMessage')}
                        </Typography>
                    </Alert>

                    <SiteValidationBanner />
                    
                    {/* Basic Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Business sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.basicInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <TextInput
                                    source="name"
                                    label={translate('resources.sites.fields.premisesName')}
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.sites.create.premisesNameHelperText')}
                                />
                                <ReferenceInput
                                    source="vendorId"
                                    reference="vendors"
                                    label={translate('resources.sites.fields.merchant')}
                                    filter={isVendorScoped ? { id: actorVendorIds } : undefined}
                                >
                                    <AutocompleteInput
                                        label={translate('resources.sites.fields.merchant')}
                                        optionText="name"
                                        fullWidth
                                        defaultValue={defaultVendorId}
                                        validate={isVendorScoped ? validateRequired : undefined}
                                        readOnly={isVendorScoped && actorVendorIds.length === 1}
                                        helperText={
                                            isVendorScoped
                                                ? translate('resources.sites.create.vendorHelperTextScoped', {
                                                      _: 'Required — restricted to your assigned merchants.',
                                                  })
                                                : translate('resources.sites.create.vendorHelperText')
                                        }
                                        sx={{ '& .MuiAutocomplete-input': { minHeight: '23px' } }}
                                    />
                                </ReferenceInput>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="latitude" 
                                            validate={validateLatitude}
                                            fullWidth
                                            step={0.000001}
                                            helperText={translate('resources.sites.create.latitudeHelperText')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <WheelSafeNumberInput 
                                            source="longitude" 
                                            validate={validateLongitude}
                                            fullWidth
                                            step={0.000001}
                                            helperText={translate('resources.sites.create.longitudeHelperText')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                <TextInput 
                                    source="addressLine1" 
                                    validate={validateRequired}
                                    fullWidth
                                    helperText={translate('resources.sites.create.addressHelperText')}
                                />
                                <TextInput 
                                    source="addressLine2" 
                                    fullWidth
                                />
                                <FormDataConsumer>
                                    {({ formData }) => (
                                        <HongKongAddressFields
                                            countryValue={formData?.country}
                                            cityValue={formData?.city}
                                            stateValue={formData?.state}
                                            validateRequired={validateRequired}
                                        />
                                    )}
                                </FormDataConsumer>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="postalCode" 
                                            fullWidth
                                            helperText={translate('resources.sites.create.postalCodeHelperText')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="timezone"
                                            choices={[
                                                { id: 'Asia/Hong_Kong', name: 'Asia/Hong Kong' },
                                                { id: 'Asia/Shanghai', name: 'Asia/Shanghai' },
                                                { id: 'Asia/Taipei', name: 'Asia/Taipei' },
                                            ]}
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Contact Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Phone sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.contactInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="phone" 
                                            fullWidth
                                            helperText={translate('resources.sites.create.phoneHelperText')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="email" 
                                            validate={validateEmail}
                                            fullWidth
                                            helperText={translate('resources.sites.create.emailHelperText')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="supportPhone" 
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <TextInput 
                                            source="website" 
                                            fullWidth
                                            helperText={translate('resources.sites.create.websiteHelperText')}
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Access & Status Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Security sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.accessStatus')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <SelectInput
                                        source="accessType"
                                        choices={[
                                            { id: 'public', name: translate('resources.sites.accessTypes.public') },
                                            { id: 'private', name: translate('resources.sites.accessTypes.private') },
                                            { id: 'restricted', name: translate('resources.sites.accessTypes.restricted') },
                                        ]}
                                        fullWidth
                                        validate={validateRequired}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput 
                                        source="isActive" 
                                        label={translate('resources.sites.create.activeSite')}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Features Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LocalParking sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.features')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                    <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                        <SelectInput
                                            source="parkingType"
                                            choices={[
                                                { id: 'street', name: translate('resources.sites.parkingTypes.street') },
                                                { id: 'lot', name: translate('resources.sites.parkingTypes.lot') },
                                                { id: 'dedicated', name: translate('resources.sites.parkingTypes.dedicated') },
                                            ]}
                                            fullWidth
                                            sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                        />
                                    </Box>
                                    <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                        <BooleanInput 
                                            source="hasCanopy" 
                                            label={translate('resources.sites.fields.hasCanopy')}
                                        />
                                    </Box>
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="reservationEnabled"
                                        label={translate('resources.sites.fields.reservationEnabled')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="emailReceiptEnabled"
                                        label={translate('resources.sites.fields.emailReceiptEnabled')}
                                        helperText={translate('resources.sites.create.emailReceiptHelperText')}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="appAccessEnabled"
                                        label={translate('resources.sites.fields.appAccessEnabled')}
                                        helperText={translate('resources.sites.create.appAccessEnabledHelperText')}
                                        defaultValue={true}
                                    />
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', '& .MuiFormControl-root': { marginTop: 0 } }}>
                                    <BooleanInput
                                        source="kioskAccessEnabled"
                                        label={translate('resources.sites.fields.kioskAccessEnabled')}
                                        helperText={translate('resources.sites.create.kioskAccessEnabledHelperText')}
                                        defaultValue={false}
                                    />
                                </Box>
                                <Box sx={{ borderTop: '1px solid', borderColor: 'divider', pt: 3, mt: 1 }}>
                                    <AmenitiesInput />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>

                    {/* Power Information Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <BatteryChargingFull sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.powerInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, alignItems: { xs: 'stretch', sm: 'flex-start' } }}>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <WheelSafeNumberInput 
                                        source="totalMaxPower" 
                                        fullWidth
                                        min={0}
                                        step={0.1}
                                        helperText={translate('resources.sites.create.totalMaxPowerHelperText')}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>
                                <Box sx={{ flex: 1, minWidth: 0, '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                    <WheelSafeNumberInput 
                                        source="gridConnectionCapacity" 
                                        fullWidth
                                        min={0}
                                        step={0.1}
                                        helperText={translate('resources.sites.create.gridCapacityHelperText')}
                                        sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                    />
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Operating Hours Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <OperatingHoursField />
                            <Box sx={{ mt: 2 }}>
                                <SelectInput
                                    source="operatingAccess"
                                    label="resources.sites.fields.operatingAccess"
                                    choices={OPERATING_ACCESS_OPTIONS}
                                    defaultValue="24_7"
                                    fullWidth
                                    helperText="resources.sites.helpers.operatingAccess"
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Parking & Restrictions Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <LocalParking sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.parkingRestrictions')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                                {/* Parking Info */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        {translate('resources.sites.fields.parkingInfo')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <WheelSafeNumberInput 
                                            source="parkingInfo.weekdayRate" 
                                            label={translate('resources.sites.fields.parkingWeekdayRate')}
                                            fullWidth
                                            helperText="Rate in dollars per hour (e.g., 25 = $25/hr)"
                                            min={0}
                                        />
                                        <WheelSafeNumberInput 
                                            source="parkingInfo.weekendRate" 
                                            label={translate('resources.sites.fields.parkingWeekendRate')}
                                            fullWidth
                                            helperText="Rate in dollars per hour (e.g., 30 = $30/hr)"
                                            min={0}
                                        />
                                        <WheelSafeNumberInput 
                                            source="parkingInfo.publicHolidayRate" 
                                            label="Public Holiday Rate"
                                            fullWidth
                                            helperText="Rate in dollars per hour for public holidays"
                                            min={0}
                                        />
                                        <WheelSafeNumberInput 
                                            source="parkingInfo.freeMinutes" 
                                            label="Free Parking Minutes"
                                            fullWidth
                                            helperText="Number of free minutes (0 = no free parking)"
                                            min={0}
                                            step={5}
                                        />
                                    </Box>
                                </Box>
                                
                                {/* Restrictions */}
                                <Box>
                                    <Typography variant="subtitle2" sx={{ mb: 2, fontWeight: 600 }}>
                                        {translate('resources.sites.fields.restrictions')}
                                    </Typography>
                                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                                        <TextInput 
                                            source="restrictions.heightLimit" 
                                            label={translate('resources.sites.fields.heightLimit')}
                                            fullWidth
                                            helperText={translate('resources.sites.helpers.heightLimit')}
                                        />
                                        <TextInput 
                                            source="restrictions.security" 
                                            label={translate('resources.sites.fields.security')}
                                            fullWidth
                                            defaultValue="24/7 CCTV"
                                            helperText={translate('resources.sites.helpers.security')}
                                        />
                                        <TextInput 
                                            source="restrictions.notes" 
                                            label={translate('resources.sites.fields.restrictionNotes')}
                                            fullWidth
                                            multiline
                                            rows={2}
                                            helperText={translate('resources.sites.helpers.restrictionNotes')}
                                        />
                                    </Box>
                                </Box>
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Images Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Photo sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.fields.images')}
                                </Typography>
                            </Box>
                            
                            <ImageUploadInput
                                source="primaryImage"
                                label={translate('resources.sites.fields.primaryImage')}
                                helperText={translate('resources.sites.helpers.primaryImage')}
                            />
                            <MultiImageUploadInput
                                source="images"
                                label={translate('resources.sites.fields.images')}
                                helperText={translate('resources.sites.helpers.images')}
                                maxImages={10}
                            />
                        </CardContent>
                    </Card>
                    
                    {/* Notes Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.fields.notes')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                <TextInput 
                                    source="notes" 
                                    fullWidth
                                    multiline
                                    rows={3}
                                    helperText={translate('resources.sites.create.notesHelperText')}
                                    sx={{ '& .MuiInputBase-root': { minHeight: '120px' } }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                    
                    {/* Multi-language Translations Section */}
                    <Card sx={{ mb: 3, overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <FormDataConsumer>
                                {({ formData }) => (
                                    <MultiLanguageFields 
                                        formData={formData}
                                        fields={[
                                            {
                                                name: 'name',
                                                label: translate('resources.sites.fields.name'),
                                                required: true,
                                                helperText: translate('resources.sites.multilang.nameHelperText')
                                            },
                                            {
                                                name: 'addressLine1',
                                                label: translate('resources.sites.fields.addressLine1'),
                                                required: true,
                                                helperText: translate('resources.sites.multilang.addressHelperText')
                                            },
                                            {
                                                name: 'addressLine2',
                                                label: translate('resources.sites.fields.addressLine2'),
                                            },
                                            {
                                                name: 'city',
                                                label: translate('resources.sites.fields.city'),
                                                required: true,
                                            },
                                            {
                                                name: 'state',
                                                label: translate('resources.sites.fields.state'),
                                                required: true,
                                            },
                                            {
                                                name: 'country',
                                                label: translate('resources.sites.fields.country'),
                                                required: true,
                                            },
                                            {
                                                name: 'notes',
                                                label: translate('resources.sites.fields.notes'),
                                                multiline: true,
                                                rows: 3,
                                                helperText: translate('resources.sites.multilang.notesHelperText')
                                            },
                                            {
                                                name: 'pricingInfo',
                                                label: translate('resources.sites.fields.pricingInfo'),
                                                multiline: true,
                                                rows: 3,
                                                helperText: translate('resources.sites.multilang.pricingInfoHelperText')
                                            }
                                        ]}
                                    />
                                )}
                            </FormDataConsumer>
                        </CardContent>
                    </Card>
                    
                    {/* Additional Information Section */}
                    <Card sx={{ overflow: 'hidden' }}>
                        <CardContent sx={{ p: { xs: 1.5, sm: 3 } }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                                <Info sx={{ mr: 2, color: '#00C853' }} />
                                <Typography variant="h6" sx={{ fontWeight: 600, fontSize: { xs: '1rem', sm: '1.25rem' } }}>
                                    {translate('resources.sites.create.additionalInfo')}
                                </Typography>
                            </Box>
                            
                            <Box sx={{ '& .MuiFormControl-root': { marginTop: 0, width: '100%' } }}>
                                <TextInput 
                                    source="qrCode" 
                                    fullWidth
                                    helperText={translate('resources.sites.create.qrCodeHelperText')}
                                    sx={{ '& .MuiInputBase-root': { height: '56px' } }}
                                />
                            </Box>
                        </CardContent>
                    </Card>
                </Box>
            </SimpleForm>
        </Create>
    );
};